import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { COLOR_THEMES, DEFAULT_THEME_ID, getColorTheme } from '../theme/colorThemes';

const STORAGE_KEY = 'bap_color_theme';

// A colour preference is meant to survive across sessions (that's the whole
// point of "picking" one), unlike the logo easter egg which is deliberately
// in-memory-only - so this one reads/writes localStorage.
const loadStoredThemeId = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return COLOR_THEMES.some((t) => t.id === stored) ? stored : DEFAULT_THEME_ID;
  } catch (error) {
    return DEFAULT_THEME_ID;
  }
};

const AppThemeContext = createContext(null);

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return context;
};

export const AppThemeProvider = ({ children }) => {
  const [themeId, setThemeIdState] = useState(loadStoredThemeId);

  const setThemeId = (id) => {
    setThemeIdState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch (error) {
      // Storage unavailable - selection still applies for this session.
    }
  };

  const muiTheme = useMemo(() => {
    const colorTheme = getColorTheme(themeId);
    return createTheme({
      palette: {
        primary: {
          main: colorTheme.primary,
          ...(colorTheme.light && { light: colorTheme.light }),
        },
        secondary: { main: colorTheme.secondary },
      },
    });
  }, [themeId]);

  const value = { themeId, setThemeId, themes: COLOR_THEMES };

  return (
    <AppThemeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};
